import React, { useCallback } from 'react';
import {
  Button,
  Card,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import type { CardProps } from 'antd';
import type { GlobalToken } from 'antd/es/theme/interface';
import { ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type {
  AbrangenciaStatusRow,
  StatusDashboardByKeyRow,
  StatusDashboardStatusSummary,
  StatusDashboardTopErrorRow,
  StatusDashboardTribunalHealth,
} from '../../../services/statusDashboardService';
import {
  DASHBOARD_OVERVIEW_BODY_MIN_HEIGHT,
  KEY_STACKED_BAR_COMPACT_HEIGHT,
  KEY_STACKED_BAR_DEFAULT_WIDTH,
  STATUS_META,
  SUMMARY_CHART_CENTER,
  SUMMARY_CHART_INNER_RADIUS,
  SUMMARY_CHART_OUTER_RADIUS,
  SUMMARY_CHART_SIZE,
  TRIBUNAL_HEALTH_META,
  TRIBUNAL_HEALTH_KEY_META,
  type SummaryStatusKey,
  type TribunalHealthKey,
  type TribunalHealthKeyType,
} from './constants';
import {
  buildCacheTagLabel,
  buildRowKey,
  createDonutSegmentPath,
  formatNumber,
  formatPercent,
  formatTableRecordSummary,
  getCacheTagTooltip,
  getSummaryChartMetrics,
} from './utils';
import { PAGE_SIZE } from './constants';

const { Text, Title } = Typography;

const CARD_DESCRIPTION_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.4,
};

const OVERVIEW_STATE_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: 120,
};

interface DonutLegendItem {
  key: string;
  label: string;
  color: string;
  total: number;
  percentage: number;
}

const renderHorizontalDonutLegend = <T extends string>(
  items: DonutLegendItem[],
  options?: {
    hoveredKey?: T | null;
    onHoverChange?: (key: T | null) => void;
  },
) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 10,
      justifyContent: 'center',
      width: '100%',
    }}
  >
    {items.map((item) => {
      const isInteractive = Boolean(options?.onHoverChange);
      const isDimmed = options?.hoveredKey && options.hoveredKey !== item.key;

      return (
        <div
          key={item.key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            cursor: isInteractive ? 'pointer' : 'default',
            opacity: isDimmed ? 0.45 : 1,
            transition: 'opacity 0.15s ease',
          }}
          onMouseEnter={isInteractive ? () => options?.onHoverChange?.(item.key as T) : undefined}
          onMouseLeave={isInteractive ? () => options?.onHoverChange?.(null) : undefined}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: item.color,
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {item.label}: {formatNumber(item.total)} ({formatPercent(item.percentage)})
          </Text>
        </div>
      );
    })}
  </div>
);

const renderCardTitleContent = (title: string, description?: string) => (
  description ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span>{title}</span>
      <Text type="secondary" style={CARD_DESCRIPTION_STYLE}>
        {description}
      </Text>
    </div>
  ) : title
);

const getCardHeaderStyles = (
  isMobile: boolean,
  description?: string,
  bodyPadding?: { mobile: number; desktop: number },
): { header?: { alignItems: 'flex-start' }; body: { padding: number } } => ({
  header: description ? { alignItems: 'flex-start' } : undefined,
  body: { padding: isMobile ? bodyPadding?.mobile ?? 12 : bodyPadding?.desktop ?? 16 },
});

const getOverviewCardLayout = (
  isMobile: boolean,
  description?: string,
): Pick<CardProps, 'style' | 'styles'> => {
  const base = getCardHeaderStyles(isMobile, description);

  return {
    style: { height: '100%', width: '100%' },
    styles: {
      ...base,
      body: {
        ...base.body,
        minHeight: isMobile ? undefined : DASHBOARD_OVERVIEW_BODY_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      },
    },
  };
};

export const getSectionPanelStyle = (
  token: GlobalToken,
  isMobile: boolean,
): React.CSSProperties => ({
  width: '100%',
  padding: isMobile ? 12 : 16,
  borderRadius: token.borderRadiusLG,
  background: token.colorFillAlter,
  border: `1px solid ${token.colorBorderSecondary}`,
  borderTop: `4px solid ${token.colorPrimary}`,
  boxShadow: token.boxShadowTertiary,
});

export const useDashboardRenderers = (isMobile: boolean) => {
  const { token } = theme.useToken();

  const renderCacheTag = useCallback((fromCache: boolean, cachedAt?: string | null) => (
    <Tooltip title={getCacheTagTooltip(fromCache, cachedAt)}>
      <Tag color={fromCache ? 'blue' : 'green'} style={{ margin: 0 }}>
        {buildCacheTagLabel(fromCache, cachedAt)}
      </Tag>
    </Tooltip>
  ), []);

  const renderCollapseHeaderExtra = useCallback((
    fromCache: boolean | undefined,
    cachedAt: string | null,
    loading: boolean,
    onRefresh?: (event: React.MouseEvent) => void,
  ) => (
    <Space size={4} onClick={(event) => event.stopPropagation()}>
      {fromCache !== undefined && !loading && renderCacheTag(fromCache, cachedAt)}
      {onRefresh && (
        <Tooltip title="Recarregar dados do banco">
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={onRefresh}
            aria-label="Recarregar"
          />
        </Tooltip>
      )}
    </Space>
  ), [renderCacheTag]);

  const renderCardExtra = useCallback((
    fromCache: boolean | undefined,
    loading: boolean,
    onRefresh?: () => void,
    cachedAt?: string | null,
  ) => {
    if (fromCache === undefined && !onRefresh) return undefined;

    return (
      <Space size={4}>
        {fromCache !== undefined && !loading && renderCacheTag(fromCache, cachedAt)}
        {onRefresh && (
          <Tooltip title={fromCache === undefined ? 'Carregar dados' : 'Recarregar dados do banco'}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={onRefresh}
              aria-label="Recarregar"
            />
          </Tooltip>
        )}
      </Space>
    );
  }, [renderCacheTag]);

  const renderKeyStackedBarLegend = useCallback(() => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 12 }}>
      {STATUS_META.map((statusMeta) => (
        <Space key={statusMeta.key} size={6}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: statusMeta.color,
              display: 'inline-block',
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {statusMeta.label}
          </Text>
        </Space>
      ))}
    </div>
  ), []);

  const renderVerticalKeyStackedBars = useCallback((
    items: StatusDashboardByKeyRow[],
    options: {
      chartHeight: number;
      barWidth?: number;
      fillWidth?: boolean;
      maxBars?: number;
      scrollable?: boolean;
      tooltipAsPercent?: boolean;
    },
  ) => {
    const visibleItems = options.maxBars ? items.slice(0, options.maxBars) : items;
    const maxTotal = visibleItems.reduce((max, item) => Math.max(max, item.total), 0) || 1;

    const formatSegmentTooltip = (
      item: StatusDashboardByKeyRow,
      statusMeta: (typeof STATUS_META)[number],
      value: number,
    ) => {
      const percentage = item.total > 0 ? (value / item.total) * 100 : 0;
      if (options.tooltipAsPercent) {
        return `${item.key} — ${statusMeta.label}: ${formatPercent(percentage)}`;
      }
      return `${item.key} — ${statusMeta.label}: ${formatNumber(value)}`;
    };

    const bars = (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: options.fillWidth ? 6 : 10,
          justifyContent: options.fillWidth ? 'stretch' : options.scrollable ? 'flex-start' : 'space-around',
          width: '100%',
          minHeight: options.chartHeight + 36,
          minWidth: options.scrollable && !options.fillWidth
            ? visibleItems.length * ((options.barWidth ?? KEY_STACKED_BAR_DEFAULT_WIDTH) + 10)
            : undefined,
          padding: '4px 0',
        }}
      >
        {visibleItems.map((item) => {
          const barHeight = Math.max((item.total / maxTotal) * options.chartHeight, item.total > 0 ? 4 : 0);

          return (
            <div
              key={item.key}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                ...(options.fillWidth
                  ? { flex: '1 1 0', minWidth: 0, width: '100%' }
                  : { width: options.barWidth ?? KEY_STACKED_BAR_DEFAULT_WIDTH, flexShrink: 0 }),
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  height: barHeight,
                  width: '100%',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: token.colorFillSecondary,
                }}
              >
                {STATUS_META.map((statusMeta) => {
                  const value = item[statusMeta.key];
                  if (!value) return null;
                  const segmentHeight = (value / item.total) * barHeight;

                  return (
                    <Tooltip
                      key={statusMeta.key}
                      title={formatSegmentTooltip(item, statusMeta, value)}
                    >
                      <div
                        style={{
                          height: segmentHeight,
                          background: statusMeta.color,
                          minHeight: value > 0 ? 2 : 0,
                          width: '100%',
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </div>
              <Tooltip title={item.key}>
                <Text
                  ellipsis
                  style={{
                    fontSize: 11,
                    marginTop: 6,
                    maxWidth: '100%',
                    width: '100%',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  {item.key}
                </Text>
              </Tooltip>
            </div>
          );
        })}
      </div>
    );

    if (options.scrollable && !options.fillWidth) {
      return (
        <div style={{ overflowX: 'auto', paddingBottom: 4, width: '100%' }}>
          {bars}
        </div>
      );
    }

    return bars;
  }, [token.colorFillSecondary]);

  const renderCollapseLabel = useCallback((
    title: string,
    extra: React.ReactNode,
    description?: string,
  ) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' }}>
        <span>{title}</span>
        {extra}
      </span>
      {description ? (
        <Text type="secondary" style={CARD_DESCRIPTION_STYLE}>
          {description}
        </Text>
      ) : null}
    </div>
  ), []);

  const renderTopErrorCard = useCallback((
    title: string,
    items: StatusDashboardTopErrorRow[] | undefined,
    accentColor: string,
    loading: boolean,
    titleDescription?: string,
    options?: {
      fromCache?: boolean;
      onRefresh?: () => void;
    emptyDescription?: string;
    cachedAt?: string | null;
    orientation?: 'horizontal' | 'vertical';
    refreshing?: boolean;
  },
) => {
    const list = Array.isArray(items) ? items : [];
    const maxTotal = list.reduce((max, item) => Math.max(max, item.total), 0) || 1;
    const verticalChartHeight = KEY_STACKED_BAR_COMPACT_HEIGHT;
    const orientation = options?.orientation ?? 'horizontal';
    const refreshLoading = options?.refreshing ?? loading;

    return (
      <Card
        title={renderCardTitleContent(title, titleDescription)}
        extra={renderCardExtra(options?.fromCache, refreshLoading, options?.onRefresh, options?.cachedAt)}
        {...getOverviewCardLayout(isMobile, titleDescription)}
      >
        {loading ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Spin />
          </div>
        ) : list.length === 0 ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Empty description={options?.emptyDescription ?? 'Sem dados para exibir'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : orientation === 'vertical' ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 10,
              justifyContent: 'space-around',
              width: '100%',
              minHeight: verticalChartHeight + 40,
              padding: '4px 0',
            }}
          >
            {list.map((item) => {
              const barHeight = Math.max((item.total / maxTotal) * verticalChartHeight, item.total > 0 ? 4 : 0);

              return (
                <div
                  key={item.label}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: '1 1 0',
                    minWidth: 0,
                  }}
                >
                  <Tooltip title={`${formatNumber(item.total)} | ${formatPercent(item.percentage)}`}>
                    <div
                      style={{
                        width: '100%',
                        maxWidth: 56,
                        height: barHeight,
                        borderRadius: '4px 4px 0 0',
                        background: accentColor,
                      }}
                    />
                  </Tooltip>
                  <Text
                    ellipsis
                    style={{
                      fontSize: 11,
                      marginTop: 8,
                      maxWidth: '100%',
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Text>
                </div>
              );
            })}
          </div>
        ) : (
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {list.map((item) => {
              const barWidth = Math.max((item.total / maxTotal) * 100, item.total > 0 ? 8 : 0);
              return (
                <div key={item.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                    <Text strong ellipsis style={{ maxWidth: '70%' }}>
                      {item.label}
                    </Text>
                    <Text type="secondary">
                      {formatNumber(item.total)} | {formatPercent(item.percentage)}
                    </Text>
                  </div>
                  <div
                    style={{
                      height: 10,
                      borderRadius: 999,
                      background: token.colorFillSecondary,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${barWidth}%`,
                        height: '100%',
                        borderRadius: 999,
                        background: accentColor,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </Space>
        )}
      </Card>
    );
  }, [isMobile, renderCardExtra, token.colorFillSecondary]);

  const renderKeyStackedBarCard = useCallback((
    items: StatusDashboardByKeyRow[] | undefined,
    loading: boolean,
    titleDescription?: string,
    emptyDescription?: string,
  ) => {
    const list = Array.isArray(items) ? items : [];

    return (
      <Card
        title={renderCardTitleContent('Por key', titleDescription)}
        {...getOverviewCardLayout(isMobile, titleDescription)}
      >
        {loading ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Spin />
          </div>
        ) : list.length === 0 ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Empty description={emptyDescription ?? 'Sem dados para exibir'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {renderVerticalKeyStackedBars(list, {
              chartHeight: KEY_STACKED_BAR_COMPACT_HEIGHT,
              fillWidth: true,
              tooltipAsPercent: true,
            })}
            {renderKeyStackedBarLegend()}
          </Space>
        )}
      </Card>
    );
  }, [isMobile, renderKeyStackedBarLegend, renderVerticalKeyStackedBars]);

  const renderTribunalHealthKeyLegend = useCallback(() => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 4 }}>
      {TRIBUNAL_HEALTH_KEY_META.map((keyMeta) => (
        <Space key={keyMeta.key} size={6}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: keyMeta.color,
              display: 'inline-block',
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {keyMeta.label}
          </Text>
        </Space>
      ))}
    </div>
  ), []);

  const renderTribunalHealthCard = useCallback((
    health: StatusDashboardTribunalHealth | null | undefined,
    loading: boolean,
    hoveredKey: TribunalHealthKey | null,
    onHoverChange: (key: TribunalHealthKey | null) => void,
    titleDescription?: string,
    emptyDescription?: string,
  ) => {
    const totalTribunais = health?.totalTribunais ?? 0;
    const metrics = TRIBUNAL_HEALTH_META.map((item) => ({
      ...item,
      total: health?.[item.key].total ?? 0,
      percentage: health?.[item.key].percentage ?? 0,
      byKey: health?.[item.key].byKey ?? {
        cnj: 0,
        doc: 0,
        nomeparte: 0,
        noeadv: 0,
        oab: 0,
      },
    }));
    const visibleMetrics = metrics.filter((item) => item.total > 0 || TRIBUNAL_HEALTH_KEY_META.some(
      (keyMeta) => (item.byKey[keyMeta.key as TribunalHealthKeyType] ?? 0) > 0,
    ));
    const maxTotal = metrics.reduce((max, item) => Math.max(max, item.total), 0) || 1;
    const rowSegmentTotal = (item: typeof metrics[number]) => TRIBUNAL_HEALTH_KEY_META.reduce(
      (sum, keyMeta) => sum + (item.byKey[keyMeta.key as TribunalHealthKeyType] ?? 0),
      0,
    );

    const renderStackedBar = (
      item: typeof metrics[number],
      barWidthPercent: number,
    ) => {
      const segmentDenominator = rowSegmentTotal(item) || 1;

      return (
        <div
          style={{
            display: 'flex',
            width: `${barWidthPercent}%`,
            minWidth: item.total > 0 ? 24 : 0,
            height: 14,
            borderRadius: 4,
            overflow: 'hidden',
            background: token.colorFillSecondary,
          }}
        >
          {TRIBUNAL_HEALTH_KEY_META.map((keyMeta) => {
            const value = item.byKey[keyMeta.key as TribunalHealthKeyType] ?? 0;
            if (!value) return null;

            const segmentWidth = (value / segmentDenominator) * 100;
            const segmentPercentage = totalTribunais > 0 ? (value / totalTribunais) * 100 : 0;

            return (
              <Tooltip
                key={keyMeta.key}
                title={`${keyMeta.label}: ${formatNumber(value)} (${formatPercent(segmentPercentage)})`}
              >
                <div
                  style={{
                    width: `${segmentWidth}%`,
                    minWidth: value > 0 ? 2 : 0,
                    height: '100%',
                    background: keyMeta.color,
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
      );
    };

    return (
      <Card
        title={renderCardTitleContent('Por situação e key', titleDescription)}
        {...getOverviewCardLayout(isMobile, titleDescription)}
      >
        {loading ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Spin />
          </div>
        ) : !health || totalTribunais === 0 ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Empty description={emptyDescription ?? 'Sem dados para exibir'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={4} style={{ marginBottom: 0 }}>
                {formatNumber(totalTribunais)}
              </Title>
              <Text type="secondary">tribunais</Text>
            </div>

            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              {visibleMetrics.map((item) => {
                const barWidth = Math.max((item.total / maxTotal) * 100, item.total > 0 ? 8 : 0);
                const isDimmed = hoveredKey && hoveredKey !== item.key;

                return (
                  <div
                    key={item.key}
                    onMouseEnter={() => onHoverChange(item.key)}
                    onMouseLeave={() => onHoverChange(null)}
                    style={{
                      opacity: isDimmed ? 0.45 : 1,
                      transition: 'opacity 0.15s ease',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                      <Text strong ellipsis style={{ maxWidth: '70%' }}>
                        {item.label}
                      </Text>
                      <Text type="secondary">
                        {formatNumber(item.total)} | {formatPercent(item.percentage)}
                      </Text>
                    </div>
                    {renderStackedBar(item, barWidth)}
                  </div>
                );
              })}
            </Space>

            {renderTribunalHealthKeyLegend()}
          </Space>
        )}
      </Card>
    );
  }, [isMobile, renderTribunalHealthKeyLegend, token.colorFillSecondary]);

  const renderSummaryCard = useCallback((
    summary: StatusDashboardStatusSummary | null | undefined,
    loading: boolean,
    hoveredKey: SummaryStatusKey | null,
    onHoverChange: (key: SummaryStatusKey | null) => void,
    titleDescription?: string,
    fromCache?: boolean,
    onRefresh?: () => void,
    emptyDescription?: string,
    cachedAt?: string | null,
    refreshing?: boolean,
  ) => {
    const metrics = getSummaryChartMetrics(summary ?? undefined);
    const visibleMetrics = metrics.filter((item) => item.total > 0);
    const refreshLoading = refreshing ?? loading;
    const summaryExtra = renderCardExtra(fromCache, refreshLoading, onRefresh, cachedAt);

    return (
      <Card
        title={renderCardTitleContent('Todas as consultas', titleDescription)}
        extra={summaryExtra}
        {...getOverviewCardLayout(isMobile, titleDescription)}
      >
        {loading ? (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Spin />
          </div>
        ) : summary ? (
          <Space direction="vertical" size={16} style={{ width: '100%', alignItems: 'center' }}>
            <div style={{ position: 'relative', width: SUMMARY_CHART_SIZE, height: SUMMARY_CHART_SIZE }}>
              <svg
                width={SUMMARY_CHART_SIZE}
                height={SUMMARY_CHART_SIZE}
                viewBox={`0 0 ${SUMMARY_CHART_SIZE} ${SUMMARY_CHART_SIZE}`}
                style={{ display: 'block' }}
                onMouseLeave={() => onHoverChange(null)}
              >
                {metrics.map((item, index) => {
                  const summaryTotal = Math.max(summary.total, 1);
                  const previousTotal = metrics
                    .slice(0, index)
                    .reduce((acc, current) => acc + current.total, 0);

                  if (item.total <= 0) return null;

                  const startAngle = (previousTotal / summaryTotal) * 360;
                  const endAngle = ((previousTotal + item.total) / summaryTotal) * 360;
                  const segmentPath = createDonutSegmentPath(
                    SUMMARY_CHART_CENTER,
                    SUMMARY_CHART_CENTER,
                    SUMMARY_CHART_INNER_RADIUS,
                    SUMMARY_CHART_OUTER_RADIUS,
                    startAngle,
                    endAngle,
                  );

                  return (
                    <path
                      key={item.key}
                      d={segmentPath}
                      fill={item.color}
                      stroke="none"
                      onMouseEnter={() => onHoverChange(item.key)}
                      style={{ cursor: 'pointer', transition: 'opacity 0.15s ease' }}
                      opacity={hoveredKey && hoveredKey !== item.key ? 0.45 : 1}
                    />
                  );
                })}
                <circle
                  cx={SUMMARY_CHART_CENTER}
                  cy={SUMMARY_CHART_CENTER}
                  r={SUMMARY_CHART_INNER_RADIUS}
                  fill={token.colorBgContainer}
                  stroke={token.colorBorderSecondary}
                  strokeWidth={1}
                />
              </svg>

              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Title level={2} style={{ marginBottom: 0 }}>
                  {formatNumber(summary.total)}
                </Title>
                <Text type="secondary">consultas</Text>
              </div>
            </div>

            {renderHorizontalDonutLegend(visibleMetrics, {
              hoveredKey,
              onHoverChange,
            })}
          </Space>
        ) : (
          <div style={OVERVIEW_STATE_CONTAINER_STYLE}>
            <Empty description={emptyDescription ?? 'Sem dados para exibir'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
      </Card>
    );
  }, [isMobile, renderCardExtra, token.colorBgContainer, token.colorBorderSecondary]);

  const renderTableCard = useCallback(<T extends AbrangenciaStatusRow>(
    title: string,
    extra: React.ReactNode,
    loading: boolean,
    data: T[],
    total: number,
    page: number,
    columns: ColumnsType<T>,
    onPageChange: (p: number) => void,
    titleDescription?: string,
  ) => (
    <Card
      title={renderCardTitleContent(title, titleDescription)}
      extra={extra}
      styles={getCardHeaderStyles(isMobile, titleDescription, { mobile: 8, desktop: 16 })}
    >
      {loading ? (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatTableRecordSummary(total, data.length)}
          </Text>
          <Table<T>
            size="small"
            rowKey={(row, index) => buildRowKey(row, index ?? 0)}
            columns={columns}
            dataSource={data}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total,
              showSizeChanger: false,
              showTotal: (paginationTotal, range) => (
                total === 0
                  ? '0 registros'
                  : `${range[0]}-${range[1]} de ${formatNumber(paginationTotal)}`
              ),
              onChange: onPageChange,
              size: isMobile ? 'small' : 'default',
            }}
          />
        </Space>
      )}
    </Card>
  ), [isMobile]);

  return {
    renderCacheTag,
    renderCollapseHeaderExtra,
    renderCollapseLabel,
    renderCardExtra,
    renderTopErrorCard,
    renderKeyStackedBarCard,
    renderTribunalHealthCard,
    renderSummaryCard,
    renderTableCard,
  };
};
